import {
    MenuOutlined,
    ArrowDropDownOutlined,
    LightModeOutlined,
    DarkModeOutlined,
    HelpOutlineOutlined,
    SettingsOutlined,
    LogoutOutlined,
    AutoAwesomeOutlined,
    InfoOutlined,
} from '@mui/icons-material'
import {
    useColorScheme,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Button,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    List,
    ListItem,
    ListItemText,
    Divider,
    SwipeableDrawer,
    Box,
} from '@mui/material'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { ButtonLink } from '@/components/ButtonLink'
import { ListItemButtonLink } from '@/components/ListItemButtonLink'
import { MenuItemLink } from '@/components/MenuItemLink'
import { NotificationsMenu } from '@/features/layout/components/NotificationsMenu'
import { generateAvatarColor } from '@/helper/generateAvatarColor'
import { useCalendarForTrackingsOverviewKey } from '@/helper/userSettingsKeys'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useBooleanUserSetting } from '@/queries/useUserSettings'

const seleniumPrefix = 'top-app-navigation'

/**
 * Displayes the top navigation.
 * This navigation is alwayes displayed for both admin and normal users.
 */
export const TopAppNavigation: React.FC<{
    isAdmin: boolean
}> = ({ isAdmin }) => {
    const [overFlowMenuEl, setOverFlowMenuEl] =
        React.useState<null | HTMLElement>(null)
    const [adminMenuEl, setAdminMenuEl] = React.useState<null | HTMLElement>(
        null
    )

    const [open, setOpen] = React.useState(false)

    const colorScheme = useColorScheme()

    const title = usePageTitle()

    const { user } = useAuth()
    const initials =
        user?.profile.given_name && user.profile.family_name ?
            `${user.profile.given_name[0]}${user.profile.family_name[0]}`.toUpperCase()
        :   ''

    const avatarColor =
        user ?
            generateAvatarColor(
                user.profile.given_name,
                user.profile.family_name,
                user.profile.email
            )
        :   undefined

    const { t } = useTranslation()

    return (
        <>
            <Box
                sx={theme => ({
                    // Keep app bar at the top of the screen.
                    position: 'sticky',
                    top: 0,
                    padding: 1,
                    paddingBottom: 1,

                    // Otherwise the elements that are scrolled behind the app bar would be visible.
                    backgroundColor: theme.vars.palette.background.default,
                    zIndex: theme.zIndex.appBar,
                })}
            >
                <AppBar
                    variant={'outlined'}
                    position={'sticky'}
                    color={'transparent'}
                    sx={{ containerType: 'inline-size' }}
                >
                    <Toolbar sx={{ gap: 1 }}>
                        {isAdmin && (
                            <IconButton
                                sx={{
                                    display: { sm: 'none', xs: 'inline-flex' },
                                }}
                                onClick={() => setOpen(true)}
                            >
                                <MenuOutlined />
                            </IconButton>
                        )}

                        <Typography
                            sx={{ display: { sm: 'none', xs: 'block' } }}
                            variant={'h6'}
                        >
                            {title}
                        </Typography>

                        <ButtonLinks
                            isAdmin={isAdmin}
                            setAdminMenuEl={setAdminMenuEl}
                        />

                        <div style={{ flex: 1 }} />

                        <NotificationsMenu />

                        <IconButton
                            onClick={() =>
                                colorScheme.setMode(
                                    colorScheme.mode === 'system' ?
                                        colorScheme.colorScheme === 'dark' ?
                                            'light'
                                        :   'dark'
                                    : colorScheme.mode === 'dark' ? 'light'
                                    : 'dark'
                                )
                            }
                            sx={{
                                display: {
                                    sm: 'inline-flex',
                                    xs: 'none',
                                },
                            }}
                            title={
                                colorScheme.mode === 'system' ?
                                    t('common.ui.systemPreferenceMode')
                                : colorScheme.mode === 'dark' ?
                                    t('common.ui.lightMode')
                                :   t('common.ui.darkMode')
                            }
                        >
                            {colorScheme.mode === 'system' ?
                                <AutoAwesomeOutlined />
                            : colorScheme.mode === 'dark' ?
                                <LightModeOutlined />
                            :   <DarkModeOutlined />}
                        </IconButton>

                        <IconButton
                            onClick={({ currentTarget }) =>
                                setOverFlowMenuEl(currentTarget)
                            }
                        >
                            <Avatar sx={{ bgcolor: avatarColor }}>
                                {initials}
                            </Avatar>
                        </IconButton>

                        <AdminMenu
                            anchorEl={adminMenuEl}
                            handleClose={() => setAdminMenuEl(null)}
                        />

                        <OverflowMenu
                            anchorEl={overFlowMenuEl}
                            handleClose={() => setOverFlowMenuEl(null)}
                        />
                    </Toolbar>
                </AppBar>
            </Box>

            <AdminNavigationDrawer
                isAdmin={isAdmin}
                open={open}
                setOpen={setOpen}
            />
        </>
    )
}

const ButtonLinks: React.FC<{
    isAdmin: boolean
    setAdminMenuEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
}> = ({ isAdmin, setAdminMenuEl }) => {
    const { t } = useTranslation()

    const { data: useCalendarForTrackings } = useBooleanUserSetting(
        useCalendarForTrackingsOverviewKey
    )

    return (
        <>
            <ButtonLink
                sx={{
                    display: {
                        sm: 'inline-flex',
                        xs: 'none',
                    },
                }}
                to={'/'}
                activeProps={{ variant: 'outlined' }}
                data-selenium={`${seleniumPrefix}-homepage`}
            >
                {t('common.homepage')}
            </ButtonLink>
            <ButtonLink
                sx={{ display: { sm: 'inline-flex', xs: 'none' } }}
                to={
                    useCalendarForTrackings ?
                        '/trackings/calendar'
                    :   '/trackings/list'
                }
                activeProps={{ variant: 'outlined' }}
                data-selenium={`${seleniumPrefix}-trackings`}
            >
                {t('common.trackings')}
            </ButtonLink>
            <ButtonLink
                sx={{
                    display: {
                        sm: 'inline-flex',
                        xs: 'none',
                    },
                }}
                to={'/statistics'}
                activeProps={{ variant: 'outlined' }}
                data-selenium={`${seleniumPrefix}-statistics`}
            >
                {t('common.statistics')}
            </ButtonLink>
            {!isAdmin && (
                <ButtonLink
                    sx={{
                        display: {
                            '@md': 'inline-flex',
                            '@xs': 'none',
                        },
                    }}
                    to={'/reports'}
                    activeProps={{ variant: 'outlined' }}
                    data-selenium={`${seleniumPrefix}-reports`}
                >
                    {t('common.reports')}
                </ButtonLink>
            )}

            {isAdmin && (
                <>
                    <Divider
                        sx={{
                            display: {
                                '@md': 'inline-flex',
                                '@xs': 'none',
                            },
                        }}
                        orientation={'vertical'}
                        flexItem={true}
                        variant={'middle'}
                    />
                    <ButtonLink
                        sx={{
                            display: {
                                '@md': 'inline-flex',
                                '@xs': 'none',
                            },
                        }}
                        to={'/admin/users'}
                        activeProps={{ variant: 'outlined' }}
                        data-selenium={`${seleniumPrefix}-user`}
                    >
                        {t('common.user')}
                    </ButtonLink>
                    <ButtonLink
                        sx={{
                            display: {
                                '@md': 'inline-flex',
                                '@xs': 'none',
                            },
                        }}
                        to={'/admin/projects'}
                        activeProps={{ variant: 'outlined' }}
                        data-selenium={`${seleniumPrefix}-projects`}
                    >
                        {t('common.projects')}
                    </ButtonLink>
                    <ButtonLink
                        sx={{
                            display: {
                                '@md': 'inline-flex',
                                '@xs': 'none',
                            },
                        }}
                        to={'/admin/reports'}
                        activeProps={{ variant: 'outlined' }}
                        data-selenium={`${seleniumPrefix}-reports-admin`}
                    >
                        {t('common.reports')}
                    </ButtonLink>

                    <Button
                        sx={{
                            display: {
                                xs: 'none',
                                sm: 'inline-flex',
                            },
                        }}
                        endIcon={<ArrowDropDownOutlined />}
                        onClick={({ currentTarget }) =>
                            setAdminMenuEl(currentTarget)
                        }
                        data-selenium={`${seleniumPrefix}-admin`}
                    >
                        {t('common.admin')}
                    </Button>
                </>
            )}
        </>
    )
}

const AdminMenu: React.FC<{
    anchorEl: HTMLElement | null
    handleClose: () => void
}> = ({ anchorEl, handleClose }) => {
    const { t } = useTranslation()

    return (
        <Menu
            anchorEl={anchorEl}
            id={'account-menu'}
            open={!!anchorEl}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{
                horizontal: 'center',
                vertical: 'top',
            }}
            anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
            }}
            disablePortal={true}
        >
            <MenuItemLink
                to={'/admin/users'}
                sx={{
                    display: {
                        '@md': 'none',
                        '@xs': 'block',
                    },
                }}
                activeProps={{
                    sx: {
                        display: {
                            '@md': 'none',
                            '@xs': 'block',
                        },
                    },
                    selected: true,
                }}
                data-selenium={`${seleniumPrefix}-users-admin-menu`}
            >
                {t('common.user')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/projects'}
                sx={{
                    display: {
                        '@md': 'none',
                        '@xs': 'block',
                    },
                }}
                activeProps={{
                    sx: {
                        display: {
                            '@md': 'none',
                            '@xs': 'block',
                        },
                    },
                    selected: true,
                }}
                data-selenium={`${seleniumPrefix}-projects-admin-menu`}
            >
                {t('common.projects')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/reports'}
                sx={{
                    display: {
                        '@md': 'none',
                        '@xs': 'block',
                    },
                }}
                activeProps={{
                    sx: {
                        display: {
                            '@md': 'none',
                            '@xs': 'block',
                        },
                    },
                    selected: true,
                }}
                data-selenium={`${seleniumPrefix}-reports-admin-menu`}
            >
                {t('common.reports')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/holidays'}
                activeProps={{ selected: true }}
                data-selenium={`${seleniumPrefix}-holidays-admin-menu`}
            >
                {t('common.holidays')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/global-settings'}
                activeProps={{ selected: true }}
                data-selenium={`${seleniumPrefix}-global-settings-admin-menu`}
            >
                {t('common.globalSettings')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/working-time-models'}
                activeProps={{ selected: true }}
                data-selenium={`${seleniumPrefix}-working-time-models-admin-menu`}
            >
                {t('common.workingTimeModel')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/businesses'}
                activeProps={{ selected: true }}
                data-selenium={`${seleniumPrefix}-businesses-admin-menu`}
            >
                {t('common.businesses')}
            </MenuItemLink>
            <MenuItemLink
                to={'/admin/statistics'}
                activeProps={{ selected: true }}
            >
                {t('common.statistics')}
            </MenuItemLink>
        </Menu>
    )
}

const OverflowMenu: React.FC<{
    anchorEl: HTMLElement | null
    handleClose: () => void
}> = ({ anchorEl, handleClose }) => {
    const colorScheme = useColorScheme()
    const { t } = useTranslation()

    const auth = useAuth()

    return (
        <Menu
            anchorEl={anchorEl}
            id={'account-menu'}
            open={!!anchorEl}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{
                horizontal: 'right',
                vertical: 'top',
            }}
            anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
            }}
        >
            <MenuItem
                onClick={() =>
                    colorScheme.setMode(
                        colorScheme.mode === 'system' ?
                            colorScheme.colorScheme === 'dark' ?
                                'light'
                            :   'dark'
                        : colorScheme.mode === 'dark' ? 'light'
                        : 'dark'
                    )
                }
                sx={{
                    display: {
                        sm: 'none',
                        xs: 'flex',
                    },
                }}
                data-selenium={`${seleniumPrefix}-color-scheme-overflow-menu`}
            >
                <ListItemIcon>
                    {colorScheme.mode === 'system' ?
                        <AutoAwesomeOutlined fontSize={'small'} />
                    : colorScheme.mode === 'dark' ?
                        <LightModeOutlined fontSize={'small'} />
                    :   <DarkModeOutlined fontSize={'small'} />}
                </ListItemIcon>

                {colorScheme.mode === 'system' ?
                    t('common.ui.systemPreferenceMode')
                : colorScheme.mode === 'dark' ?
                    t('common.ui.lightMode')
                :   t('common.ui.darkMode')}
            </MenuItem>
            <MenuItem
                onClick={handleClose}
                data-selenium={`${seleniumPrefix}-help-overflow-menu`}
            >
                <ListItemIcon>
                    <HelpOutlineOutlined fontSize={'small'} />
                </ListItemIcon>
                {t('common.help')}
            </MenuItem>
            <MenuItemLink
                onClick={handleClose}
                to={'/about'}
                data-selenium={`${seleniumPrefix}-about-overflow-menu`}
            >
                <ListItemIcon>
                    <InfoOutlined fontSize={'small'} />
                </ListItemIcon>
                {t('common.about')}
            </MenuItemLink>
            <MenuItemLink
                onClick={handleClose}
                sx={{
                    display: {
                        sm: 'flex',
                        xs: 'none',
                    },
                }}
                to={'/settings'}
                data-selenium={`${seleniumPrefix}-settings-overflow-menu`}
            >
                <ListItemIcon>
                    <SettingsOutlined fontSize={'small'} />
                </ListItemIcon>

                {t('common.settings')}
            </MenuItemLink>
            <MenuItem
                onClick={async () => {
                    await auth.signoutRedirect()

                    handleClose()
                }}
                data-selenium={`${seleniumPrefix}-signOut-overflow-menu`}
            >
                <ListItemIcon>
                    <LogoutOutlined fontSize={'small'} />
                </ListItemIcon>
                {t('common.signOut')}
            </MenuItem>
        </Menu>
    )
}

/**
 * Is only used for admin users on small screens, since they have many more navigation links than normal users.
 */
const AdminNavigationDrawer: React.FC<{
    isAdmin: boolean
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isAdmin, open, setOpen }) => {
    const { t } = useTranslation()

    const handleClose = () => setOpen(false)

    return (
        isAdmin && (
            <SwipeableDrawer
                anchor={'left'}
                onOpen={() => setOpen(true)}
                open={open && isAdmin}
                onClose={handleClose}
            >
                <List>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-homepage-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.homepage')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/trackings/list'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-trackings-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.trackings')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/statistics'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                        >
                            <ListItemText>
                                {t('common.statistics')}
                            </ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/settings'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-settings-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.settings')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>

                    <Divider />

                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/users'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-users-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.user')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/projects'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-projects-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.projects')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/reports'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-reports-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.reports')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/holidays'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-holidays-admin-navigation-drawer`}
                        >
                            <ListItemText>{t('common.holidays')}</ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/global-settings'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-global-settings-admin-navigation-drawer`}
                        >
                            <ListItemText>
                                {t('common.globalSettings')}
                            </ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/businesses'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-businesses-admin-navigation-drawer`}
                        >
                            <ListItemText>
                                {t('common.businesses')}
                            </ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/working-time-models'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-working-time-model-admin-navigation-drawer`}
                        >
                            <ListItemText>
                                {t('common.workingTimeModel')}
                            </ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                    <ListItem disableGutters={true} disablePadding={true}>
                        <ListItemButtonLink
                            to={'/admin/statistics'}
                            activeProps={{ selected: true }}
                            onClick={handleClose}
                        >
                            <ListItemText>
                                {t('common.statistics')}
                            </ListItemText>
                        </ListItemButtonLink>
                    </ListItem>
                </List>
            </SwipeableDrawer>
        )
    )
}
